import { DataTable } from '@/components/table/DataTable';
import { TanstackSortableColumnHeader } from '@/components/table/SortColumnHeader';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  useAdminCountriesService,
  useAdminCreateCountryService,
  useAdminUpdateCountryService,
  useAdminDeleteCountryService,
  useAdminSourcesService,
} from '@/services/backoffice';
import { getGeographyFlag, getGeographyLabel } from '@/shared/geography';
import type { CountryItem, MedtechRegulatoryData } from '@ditto/shared';
import type { ColumnDef, Row, SortingState } from '@tanstack/react-table';
import { getSortedRowModel } from '@tanstack/react-table';
import {
  ChevronDown,
  ChevronUp,
  Globe,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import { type FC, useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

export const CountriesTab: FC = () => {
  const { countries, isLoading } = useAdminCountriesService();
  const { deleteCountry } = useAdminDeleteCountryService();
  const { createCountry } = useAdminCreateCountryService();
  const { updateCountry } = useAdminUpdateCountryService();
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<CountryItem | null>(null);
  const [importing, setImporting] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);

  const handleBulkImport = (file: File) => {
    const reader = new FileReader();
    reader.onload = async e => {
      try {
        const data = JSON.parse(e.target?.result as string) as Record<
          string,
          MedtechRegulatoryData
        >;
        setImporting(true);
        let created = 0;
        let updated = 0;
        for (const [code, regData] of Object.entries(data)) {
          const existing = countries.find(
            c => c.stringId === code.toLowerCase()
          );
          if (existing) {
            updateCountry({
              id: existing.id,
              body: { medtechRegulatoryData: regData },
            });
            updated++;
          } else {
            createCountry({
              stringId: code.toLowerCase(),
              medtechRegulatoryData: regData,
            });
            created++;
          }
        }
        toast.success(
          `Imported ${created + updated} countries (${created} created, ${updated} updated)`
        );
        setImporting(false);
      } catch {
        toast.error('Invalid JSON file');
        setImporting(false);
      }
    };
    reader.readAsText(file);
  };

  const columns = useMemo<ColumnDef<CountryItem>[]>(
    () => [
      {
        id: 'flag',
        header: 'Flag',
        meta: { headerClassName: 'w-[60px]' },
        cell: ({ row }) => {
          const flag = getGeographyFlag(row.original.stringId);
          return flag ? (
            <img
              src={flag}
              alt={row.original.stringId}
              className="h-4 w-6 object-contain"
            />
          ) : (
            <Globe className="size-4 text-neutral-400" />
          );
        },
      },
      {
        accessorKey: 'stringId',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            Code
          </TanstackSortableColumnHeader>
        ),
        meta: { headerClassName: 'w-[100px]' },
        cell: ({ row }) => (
          <span className="font-mono text-xs uppercase">
            {row.original.stringId}
          </span>
        ),
      },
      {
        id: 'country',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            Country
          </TanstackSortableColumnHeader>
        ),
        cell: ({ row }) => (
          <span className="font-medium">
            {getGeographyLabel(row.original.stringId)}
          </span>
        ),
      },
      {
        id: 'regulatoryData',
        header: 'Regulatory Data',
        cell: ({ row }) => {
          const data = row.original.medtechRegulatoryData;
          const hasData = data && Object.keys(data).length > 0;
          if (!hasData) {
            return <span className="text-xs text-neutral-400">No data</span>;
          }
          return (
            <button
              onClick={e => {
                e.stopPropagation();
                row.toggleExpanded();
              }}
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
            >
              {row.getIsExpanded() ? (
                <>
                  Hide details <ChevronUp className="size-3" />
                </>
              ) : (
                <>
                  Show details <ChevronDown className="size-3" />
                </>
              )}
            </button>
          );
        },
      },
      {
        id: 'actions',
        header: () => <div className="text-right">Actions</div>,
        meta: { headerClassName: 'w-[80px]', cellClassName: 'text-right' },
        cell: ({ row }) => (
          <div onClick={e => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setEditItem(row.original)}>
                  <Pencil className="size-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-red-600"
                  onClick={() => {
                    deleteCountry(row.original.id, {
                      onSuccess: () =>
                        toast.success(
                          `Deleted ${getGeographyLabel(row.original.stringId)}`
                        ),
                      onError: (e: Error) => toast.error(e.message),
                    });
                  }}
                >
                  <Trash2 className="size-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    [deleteCountry, setEditItem]
  );

  const getRowCanExpand = useCallback((row: Row<CountryItem>) => {
    const data = row.original.medtechRegulatoryData;
    return !!data && Object.keys(data).length > 0;
  }, []);

  const renderSubComponent = useCallback(
    (row: Row<CountryItem>) => (
      <div className="px-2 py-2 bg-neutral-50">
        <RegulatoryDataPreview data={row.original.medtechRegulatoryData!} />
      </div>
    ),
    []
  );

  if (isLoading) return <div className="p-4 text-neutral-500">Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Countries</h2>
          <p className="text-sm text-muted-foreground">
            Manage medtech regulatory data per country
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4 mr-1" />
            Add Country
          </Button>
        </div>
      </div>

      <JsonDropZone onFile={handleBulkImport}>
        <div className="border border-dashed rounded-md p-3 text-center text-xs text-neutral-400">
          {importing
            ? 'Importing...'
            : 'Drop a bulk JSON file to import multiple countries (e.g. {"us": {...}, "eu": {...}})'}
        </div>
      </JsonDropZone>

      <DataTable
        columns={columns}
        data={countries}
        hidePagination
        onRowClick={country => setEditItem(country)}
        getRowCanExpand={getRowCanExpand}
        renderSubComponent={renderSubComponent}
        options={{
          getRowId: row => row.id,
          onSortingChange: setSorting,
          getSortedRowModel: getSortedRowModel(),
          enableMultiSort: false,
          state: { sorting },
        }}
      />

      <CountryModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
      />

      {editItem && (
        <CountryModal
          open={!!editItem}
          onOpenChange={open => !open && setEditItem(null)}
          mode="edit"
          country={editItem}
        />
      )}
    </div>
  );
};

const RegulatoryDataPreview: FC<{ data: MedtechRegulatoryData }> = ({
  data,
}) => (
  <pre className="text-xs font-mono text-neutral-600 whitespace-pre-wrap max-h-[200px] overflow-y-auto">
    {JSON.stringify(data, null, 2)}
  </pre>
);

// ── JSON Drop Zone ──

const JsonDropZone: FC<{
  onFile: (file: File) => void;
  children: React.ReactNode;
}> = ({ onFile, children }) => {
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div
      className={`relative rounded-md ${dragging ? 'ring-2 ring-blue-400 bg-blue-50/50' : ''}`}
      onDragOver={e => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => {
        e.preventDefault();
        setDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) onFile(file);
      }}
    >
      {children}
      <div className="flex items-center gap-2 mt-1.5">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
        >
          <Upload className="size-3" />
          Upload JSON file
        </button>
        <span className="text-xs text-neutral-400">or drag & drop</span>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          className="hidden"
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) onFile(file);
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );
};

// ── Create/Edit Modal ──

type Agency = {
  title: string;
  subtitle?: string;
  description?: string;
  link?: string;
};
type Regulation = { title: string; description?: string; link?: string };
type SourceRef = { id: string };

const CountryModal: FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  country?: CountryItem;
}> = ({ open, onOpenChange, mode, country }) => {
  const existingData = country?.medtechRegulatoryData;

  const [stringId, setStringId] = useState(country?.stringId ?? '');

  // Hero
  const [overview, setOverview] = useState(existingData?.hero?.overview ?? '');
  const [classificationSystem, setClassificationSystem] = useState(
    existingData?.hero?.classificationSystem ?? ''
  );
  const [competentAuthority, setCompetentAuthority] = useState(
    existingData?.hero?.competentAuthority ?? ''
  );

  // Sources
  const [sources, setSources] = useState<SourceRef[]>(
    existingData?.sources ?? []
  );

  // Agencies
  const [agencies, setAgencies] = useState<Agency[]>(
    existingData?.agencies ?? []
  );

  // Regulations
  const [regulations, setRegulations] = useState<Regulation[]>(
    existingData?.regulations ?? []
  );

  // Device classification
  const [mdClasses, setMdClasses] = useState(
    existingData?.deviceClassificationMd?.classes?.join(', ') ?? ''
  );
  const [mdDescription, setMdDescription] = useState(
    existingData?.deviceClassificationMd?.description ?? ''
  );
  const [mdLink, setMdLink] = useState(
    existingData?.deviceClassificationMd?.link ?? ''
  );
  const [ivdClasses, setIvdClasses] = useState(
    existingData?.deviceClassificationIvd?.classes?.join(', ') ?? ''
  );
  const [ivdDescription, setIvdDescription] = useState(
    existingData?.deviceClassificationIvd?.description ?? ''
  );
  const [ivdLink, setIvdLink] = useState(
    existingData?.deviceClassificationIvd?.link ?? ''
  );

  // JSON
  const [rawJson, setRawJson] = useState(
    JSON.stringify(existingData ?? {}, null, 2)
  );
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [showJson, setShowJson] = useState(false);

  const syncFieldsFromData = (data: MedtechRegulatoryData) => {
    setOverview(data.hero?.overview ?? '');
    setClassificationSystem(data.hero?.classificationSystem ?? '');
    setCompetentAuthority(data.hero?.competentAuthority ?? '');
    setSources(data.sources ?? []);
    setAgencies(data.agencies ?? []);
    setRegulations(data.regulations ?? []);
    setMdClasses(data.deviceClassificationMd?.classes?.join(', ') ?? '');
    setMdDescription(data.deviceClassificationMd?.description ?? '');
    setMdLink(data.deviceClassificationMd?.link ?? '');
    setIvdClasses(data.deviceClassificationIvd?.classes?.join(', ') ?? '');
    setIvdDescription(data.deviceClassificationIvd?.description ?? '');
    setIvdLink(data.deviceClassificationIvd?.link ?? '');
  };

  const handleJsonFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      try {
        const parsed = JSON.parse(text) as MedtechRegulatoryData;
        setRawJson(JSON.stringify(parsed, null, 2));
        setJsonError(null);
        syncFieldsFromData(parsed);
      } catch {
        setRawJson(text);
        setJsonError('Invalid JSON');
      }
    };
    reader.readAsText(file);
  };

  const setJsonSafe = (json: string) => {
    setRawJson(json);
    try {
      const parsed = JSON.parse(json) as MedtechRegulatoryData;
      setJsonError(null);
      syncFieldsFromData(parsed);
    } catch {
      setJsonError('Invalid JSON');
    }
  };

  const { createCountry, isPending: isCreating } =
    useAdminCreateCountryService();
  const { updateCountry, isPending: isUpdating } =
    useAdminUpdateCountryService();

  const buildData = (): MedtechRegulatoryData | null => {
    const data: MedtechRegulatoryData = {};
    const hasHero =
      overview.trim() ||
      classificationSystem.trim() ||
      competentAuthority.trim();
    if (hasHero) {
      data.hero = {};
      if (overview.trim()) data.hero.overview = overview.trim();
      if (classificationSystem.trim())
        data.hero.classificationSystem = classificationSystem.trim();
      if (competentAuthority.trim())
        data.hero.competentAuthority = competentAuthority.trim();
    }
    if (sources.length > 0) data.sources = sources.filter(s => s.id.trim());
    if (agencies.length > 0)
      data.agencies = agencies.filter(a => a.title.trim());
    if (regulations.length > 0)
      data.regulations = regulations.filter(r => r.title.trim());
    const parseCls = (s: string) =>
      s
        .split(',')
        .map(c => c.trim())
        .filter(Boolean);
    if (mdClasses.trim() || mdDescription.trim()) {
      data.deviceClassificationMd = {
        classes: parseCls(mdClasses),
        description: mdDescription.trim() || undefined,
        link: mdLink.trim() || undefined,
      };
    }
    if (ivdClasses.trim() || ivdDescription.trim()) {
      data.deviceClassificationIvd = {
        classes: parseCls(ivdClasses),
        description: ivdDescription.trim() || undefined,
        link: ivdLink.trim() || undefined,
      };
    }
    return Object.keys(data).length > 0 ? data : null;
  };

  const handleSubmit = () => {
    const regData = buildData();
    if (mode === 'create') {
      if (!stringId.trim()) {
        toast.error('Country code is required');
        return;
      }
      createCountry(
        {
          stringId: stringId.trim().toLowerCase(),
          medtechRegulatoryData: regData,
        },
        {
          onSuccess: () => {
            toast.success('Country created');
            onOpenChange(false);
          },
          onError: (e: Error) => toast.error(e.message),
        }
      );
    } else if (country) {
      updateCountry(
        { id: country.id, body: { medtechRegulatoryData: regData } },
        {
          onSuccess: () => {
            toast.success('Country updated');
            onOpenChange(false);
          },
          onError: (e: Error) => toast.error(e.message),
        }
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
          <DialogTitle>
            {mode === 'create'
              ? 'Add Country'
              : `Edit ${getGeographyLabel(country?.stringId ?? '')}`}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 px-6 pb-4 flex-1 overflow-y-auto">
          {/* JSON upload */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Import JSON</Label>
              <button
                onClick={() => setShowJson(!showJson)}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                {showJson ? 'Hide raw JSON' : 'Show raw JSON'}
              </button>
            </div>
            {showJson && (
              <JsonDropZone onFile={handleJsonFile}>
                <Textarea
                  value={rawJson}
                  onChange={e => setJsonSafe(e.target.value)}
                  className="font-mono text-xs min-h-[200px] max-h-[300px]"
                  spellCheck={false}
                />
              </JsonDropZone>
            )}
            {!showJson && (
              <JsonDropZone onFile={handleJsonFile}>
                <div className="border border-dashed rounded-md p-3 text-center text-xs text-neutral-400">
                  Drop a JSON file here to import
                </div>
              </JsonDropZone>
            )}
            {jsonError && <p className="text-xs text-red-500">{jsonError}</p>}
          </div>

          {/* Country code (create only) */}
          {mode === 'create' && (
            <div className="space-y-2">
              <Label>Country Code</Label>
              <Input
                value={stringId}
                onChange={e => setStringId(e.target.value)}
                placeholder="e.g. us, fr, de"
                className="font-mono uppercase"
              />
              {stringId && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  {getGeographyFlag(stringId) && (
                    <img
                      src={getGeographyFlag(stringId)!}
                      alt=""
                      className="h-3 w-4 object-contain"
                    />
                  )}
                  {getGeographyLabel(stringId)}
                </div>
              )}
            </div>
          )}

          {/* Hero */}
          <div className="space-y-3 rounded-lg border p-4">
            <Label className="text-base font-semibold">Hero</Label>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Overview</Label>
              <Textarea
                value={overview}
                onChange={e => setOverview(e.target.value)}
                placeholder="Regulatory overview..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  Classification System
                </Label>
                <Input
                  value={classificationSystem}
                  onChange={e => setClassificationSystem(e.target.value)}
                  placeholder="e.g. Risk-based"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  Competent Authority
                </Label>
                <Input
                  value={competentAuthority}
                  onChange={e => setCompetentAuthority(e.target.value)}
                  placeholder="e.g. FDA"
                />
              </div>
            </div>
          </div>

          {/* Sources */}
          <SourcesSelector
            countryCode={
              mode === 'create' ? stringId : (country?.stringId ?? '')
            }
            selected={sources}
            onChange={setSources}
          />

          {/* Agencies */}
          <ListSection
            title="Main Regulatory Agencies"
            items={agencies}
            onAdd={() => setAgencies(prev => [...prev, { title: '' }])}
            onRemove={i => setAgencies(prev => prev.filter((_, j) => j !== i))}
            renderItem={(item, i) => (
              <div className="space-y-2 flex-1">
                <Input
                  value={item.title}
                  onChange={e =>
                    setAgencies(prev =>
                      prev.map((a, j) =>
                        j === i ? { ...a, title: e.target.value } : a
                      )
                    )
                  }
                  placeholder="Title"
                />
                <Input
                  value={item.subtitle ?? ''}
                  onChange={e =>
                    setAgencies(prev =>
                      prev.map((a, j) =>
                        j === i ? { ...a, subtitle: e.target.value } : a
                      )
                    )
                  }
                  placeholder="Subtitle"
                />
                <Input
                  value={item.description ?? ''}
                  onChange={e =>
                    setAgencies(prev =>
                      prev.map((a, j) =>
                        j === i ? { ...a, description: e.target.value } : a
                      )
                    )
                  }
                  placeholder="Description"
                />
                <Input
                  value={item.link ?? ''}
                  onChange={e =>
                    setAgencies(prev =>
                      prev.map((a, j) =>
                        j === i ? { ...a, link: e.target.value } : a
                      )
                    )
                  }
                  placeholder="https://..."
                />
              </div>
            )}
          />

          {/* Regulations */}
          <ListSection
            title="Main Regulations & Guidances"
            items={regulations}
            onAdd={() => setRegulations(prev => [...prev, { title: '' }])}
            onRemove={i =>
              setRegulations(prev => prev.filter((_, j) => j !== i))
            }
            renderItem={(item, i) => (
              <div className="space-y-2 flex-1">
                <Input
                  value={item.title}
                  onChange={e =>
                    setRegulations(prev =>
                      prev.map((r, j) =>
                        j === i ? { ...r, title: e.target.value } : r
                      )
                    )
                  }
                  placeholder="Title"
                />
                <Input
                  value={item.description ?? ''}
                  onChange={e =>
                    setRegulations(prev =>
                      prev.map((r, j) =>
                        j === i ? { ...r, description: e.target.value } : r
                      )
                    )
                  }
                  placeholder="Description"
                />
                <Input
                  value={item.link ?? ''}
                  onChange={e =>
                    setRegulations(prev =>
                      prev.map((r, j) =>
                        j === i ? { ...r, link: e.target.value } : r
                      )
                    )
                  }
                  placeholder="https://..."
                />
              </div>
            )}
          />

          {/* Device Classification — Medical Device */}
          <div className="space-y-3 rounded-lg border p-4">
            <Label className="text-base font-semibold">
              Device Classification — Medical Devices
            </Label>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                Classes (comma-separated)
              </Label>
              <Input
                value={mdClasses}
                onChange={e => setMdClasses(e.target.value)}
                placeholder="e.g. Class I, Class IIa, Class IIb, Class III"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                Description
              </Label>
              <Textarea
                value={mdDescription}
                onChange={e => setMdDescription(e.target.value)}
                placeholder="Description..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Link</Label>
              <Input
                value={mdLink}
                onChange={e => setMdLink(e.target.value)}
                placeholder="https://..."
              />
            </div>
          </div>

          {/* Device Classification — IVD */}
          <div className="space-y-3 rounded-lg border p-4">
            <Label className="text-base font-semibold">
              Device Classification — In Vitro Devices
            </Label>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                Classes (comma-separated)
              </Label>
              <Input
                value={ivdClasses}
                onChange={e => setIvdClasses(e.target.value)}
                placeholder="e.g. Class A, Class B, Class C, Class D"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                Description
              </Label>
              <Textarea
                value={ivdDescription}
                onChange={e => setIvdDescription(e.target.value)}
                placeholder="Description..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Link</Label>
              <Input
                value={ivdLink}
                onChange={e => setIvdLink(e.target.value)}
                placeholder="https://..."
              />
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 px-6 py-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isCreating || isUpdating}>
            {mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Sources selector (filtered by country) ──

const SourcesSelector: FC<{
  countryCode: string;
  selected: SourceRef[];
  onChange: (sources: SourceRef[]) => void;
}> = ({ countryCode, selected, onChange }) => {
  const { sources } = useAdminSourcesService();

  const countrySources = useMemo(() => {
    if (!countryCode) return sources;
    const code = countryCode.toLowerCase();
    return sources.filter(
      s =>
        s.geography === code || s.institutionGeographies?.some(g => g === code)
    );
  }, [sources, countryCode]);

  const selectedIds = new Set(selected.map(s => s.id));

  const toggleSource = (id: string) => {
    if (selectedIds.has(id)) {
      onChange(selected.filter(s => s.id !== id));
    } else {
      onChange([...selected, { id }]);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <Label className="text-base font-semibold">
        Qalico Sources (Regulatory Source IDs)
      </Label>
      {countrySources.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {countryCode
            ? `No sources found for "${countryCode}"`
            : 'Enter a country code to see available sources'}
        </p>
      ) : (
        <div className="space-y-1 max-h-[200px] overflow-y-auto">
          {countrySources.map(s => (
            <label
              key={s.id}
              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-neutral-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedIds.has(s.id)}
                onChange={() => toggleSource(s.id)}
                className="rounded border-neutral-300"
              />
              <span className="text-sm">{s.title}</span>
              <span className="text-xs text-muted-foreground font-mono ml-auto">
                {s.id}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Reusable list section ──

const ListSection = <T,>({
  title,
  items,
  onAdd,
  onRemove,
  renderItem,
}: {
  title: string;
  items: T[];
  onAdd: () => void;
  onRemove: (i: number) => void;
  renderItem: (item: T, i: number) => React.ReactNode;
}) => (
  <div className="space-y-3 rounded-lg border p-4">
    <div className="flex items-center justify-between">
      <Label className="text-base font-semibold">{title}</Label>
      <Button variant="outline" size="sm" onClick={onAdd}>
        <Plus className="size-3 mr-1" />
        Add
      </Button>
    </div>
    {items.map((item, i) => (
      <div key={i} className="flex gap-2 items-start">
        {renderItem(item, i)}
        <Button
          variant="ghost"
          size="sm"
          className="h-9 w-9 p-0 shrink-0"
          onClick={() => onRemove(i)}
        >
          <Trash2 className="size-4 text-red-500" />
        </Button>
      </div>
    ))}
    {items.length === 0 && (
      <p className="text-xs text-muted-foreground">None added yet.</p>
    )}
  </div>
);
